/** AreaCalc — (dose / per_area) × areaSqIn = total dose. */
import { useState } from 'react';
import type { Calculator } from '@reefapp/shared';
import type { AreaOption } from '@reefapp/shared';

type AreaCalcShape = Calculator & { kind: 'area' };

export interface AreaCalcProps {
  calculator: AreaCalcShape;
  areaSqIn?: number;
}

function fmt(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2).replace(/\.?0+$/, '') : '—';
}

export function AreaCalc({ calculator }: AreaCalcProps) {
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const [areaSqIn, setAreaSqIn] = useState<number>(0);

  const option = calculator.options[selectedOptionIdx];
  if (!option) return null;

  const dose =
    areaSqIn > 0 ? (option.dose / option.per_area) * areaSqIn : 0;
  const hasArea = areaSqIn > 0;

  return (
    <div>
      <h3 className="text-lg font-semibold text-cyan-300 mb-4">
        Area-Based Dosage
      </h3>

      {/* Area input */}
      <div className="mb-4">
        <label
          htmlFor="area-input"
          className="block text-sm font-medium text-slate-400 mb-2"
        >
          Substrate Area (sq. inches):
        </label>
        <input
          id="area-input"
          type="number"
          min="0"
          step="1"
          value={areaSqIn || ''}
          onChange={(e) => setAreaSqIn(Number(e.target.value))}
          placeholder="e.g. 288"
          className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500"
        />
      </div>

      {/* Option selector */}
      {calculator.options.length > 1 && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-400 mb-2">
            Dosage Scenario:
          </label>
          <select
            value={selectedOptionIdx}
            onChange={(e) => setSelectedOptionIdx(Number(e.target.value))}
            className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 px-3 text-sm text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            {calculator.options.map((o: AreaOption, i: number) => (
              <option key={i} value={i}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Result */}
      <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
        <p className="text-sm text-slate-400">
          {option.name}:{' '}
          <span className="text-slate-300">
            {option.dose} {option.dose_unit} / {option.per_area} in²
          </span>
        </p>

        {hasArea ? (
          <div className="mt-3">
            <p className="text-2xl font-bold text-cyan-300">
              {fmt(dose)} {option.dose_unit}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              For {fmt(areaSqIn)} sq. inches
            </p>
          </div>
        ) : (
          <p className="mt-3 text-sm text-amber-400">
            Enter the substrate area above to calculate the dose.
          </p>
        )}

        {option.notes && (
          <p className="text-xs text-slate-500 mt-2">{option.notes}</p>
        )}
      </div>

      {calculator.notes && (
        <div className="mt-4 p-3 bg-slate-900/50 border border-slate-700 rounded-lg">
          <p className="text-xs text-slate-400 whitespace-pre-wrap">
            {calculator.notes}
          </p>
        </div>
      )}
    </div>
  );
}